import styles from './index.module.scss'
import { Checkbox, message } from 'antd'
import Utils from '@utils/utils'
import Iconfont from '@utils/Iconfont'
import { DownloadOutlined, EllipsisOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import PSAIModal from '@PSAIComponents/PSAIModal'
import { useNavigate } from 'react-router-dom'
import API, { commonApiInvoker } from '@api/api.js'
import { throttle } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { IMAGE_TYPE, TYPETITLEMAP } from '@utils/CONST'
import { useContext } from 'react'
import { AppContext } from '@utils/AppContext'
import ProjectContextMenu from '@view/page_v3/FreeCanvas/Sidebar/components/ProjectContextMenu'
import {
    duplicateFlowProject,
    getFlowProjectHistoryImagesBatch,
    saveFlowProject,
} from '@view/page_v3/FreeCanvas/execution'

const FLOW_OPS_BY = 'zp6077'
const INVALID_FILE_NAME_REG = /[\\/:*?"<>|]/g

const sanitizeFileName = (value = '') => String(value || '').replace(INVALID_FILE_NAME_REG, '_').trim()

const getUniqueZipPath = (filePath, usedPaths) => {
    if (!usedPaths.has(filePath)) {
        usedPaths.add(filePath)
        return filePath
    }

    const extensionIndex = filePath.lastIndexOf('.')
    const basePath = extensionIndex > filePath.lastIndexOf('/') ? filePath.slice(0, extensionIndex) : filePath
    const extension = extensionIndex > filePath.lastIndexOf('/') ? filePath.slice(extensionIndex) : ''
    let suffix = 2
    let uniquePath = `${basePath}(${suffix})${extension}`

    while (usedPaths.has(uniquePath)) {
        suffix += 1
        uniquePath = `${basePath}(${suffix})${extension}`
    }

    usedPaths.add(uniquePath)
    return uniquePath
}

const getImageExtension = (blob, url = '') => {
    const mimeExtension = blob?.type?.split('/')?.[1]?.split('+')?.[0]
    if (mimeExtension) return mimeExtension
    const cleanUrl = String(url || '').split('?')[0]
    const matched = cleanUrl.match(/\.([a-zA-Z0-9]+)$/)
    return matched?.[1] || 'png'
}

const ProjectListItem = (props) => {
    const navigate = useNavigate()
    const { isSelected, item, selectedList, flowCreativePreview = false, isEnterpriseMemberTab = false } = props
    const [downLoading, setDownLoading] = useState(false)
    const [contextMenu, setContextMenu] = useState({
        visible: false,
        x: 0,
        y: 0,
    })
    const [editingFlowTitle, setEditingFlowTitle] = useState(false)
    const [editingValue, setEditingValue] = useState('')
    const [renameSubmitting, setRenameSubmitting] = useState(false)
    const [actionSubmitting, setActionSubmitting] = useState(false)
    const renameInputRef = useRef(null)
    const { proSidebarValue } = useContext(AppContext)

    useEffect(() => {
        if (!contextMenu.visible) return undefined

        const handlePointerDown = (event) => {
            const target = event.target instanceof Element ? event.target : null
            if (target?.closest('[data-free-canvas-sidebar-menu="true"]')) return
            setContextMenu((prev) => ({ ...prev, visible: false }))
        }

        document.addEventListener('mousedown', handlePointerDown)
        return () => {
            document.removeEventListener('mousedown', handlePointerDown)
        }
    }, [contextMenu.visible])

    useEffect(() => {
        if (!editingFlowTitle || !renameInputRef.current) return
        renameInputRef.current.focus()
        renameInputRef.current.select()
    }, [editingFlowTitle])

    const deleteClick = () => {
        PSAIModal.confirm({
            title: '删除项目',
            content: '删除后项目将无法找回，确定删除吗？',
            onOk: deleteProject,
        })
    }

    const deleteProject = () => {
        if (item?.flowProject) {
            commonApiInvoker(API.deleteFlowProject({
                projectIds: [item?.id],
                opsBy: FLOW_OPS_BY,
            }), false, true).then(() => {
                message.success('项目已删除')
                props.listProjectv2()
            })
            return
        }
        commonApiInvoker(API.deleteProject({
            projectId: item?.id,
        })).then(() => {
            message.success('删除成功！')
            props.listProjectv2()
        })
    }

    const closeContextMenu = () => {
        setContextMenu((prev) => ({ ...prev, visible: false }))
    }

    const handleFlowMenuTriggerClick = (event) => {
        event.stopPropagation()
        const triggerRect = event.currentTarget.getBoundingClientRect()
        setContextMenu({
            visible: true,
            x: triggerRect.right - 110,
            y: triggerRect.bottom + 8,
        })
    }

    const handleFlowRenameStart = () => {
        closeContextMenu()
        setEditingFlowTitle(true)
        setEditingValue(item?.projectName || '')
    }

    const handleFlowRenameCancel = () => {
        if (renameSubmitting) return
        setEditingFlowTitle(false)
        setEditingValue('')
    }

    const handleFlowRenameSubmit = async () => {
        const trimmedValue = editingValue.trim()
        if (!trimmedValue) {
            message.warning('请输入项目名称')
            return
        }

        setRenameSubmitting(true)
        try {
            await saveFlowProject({
                id: item?.id,
                projectName: trimmedValue,
                cover: item?.cover || '',
                successMessage: '重命名成功',
            })
            setEditingFlowTitle(false)
            setEditingValue('')
            props.listProjectv2?.()
        } catch (error) {
            console.error(error)
            message.error('重命名失败')
        } finally {
            setRenameSubmitting(false)
        }
    }

    const handleFlowDuplicate = async () => {
        closeContextMenu()
        setActionSubmitting(true)
        try {
            const nextProject = await duplicateFlowProject({
                projectId: item?.id,
                projectName: item?.projectName || '未命名画布',
                cover: item?.cover || '',
                successMessage: '副本已创建',
            })
            props.listProjectv2?.()
            navigate(
                `/home/freeCanvas?project_id=${encodeURIComponent(nextProject.id || '')}&project_name=${encodeURIComponent(nextProject.name || '')}`,
                projectDetailState ? { state: projectDetailState } : undefined,
            )
        } catch (error) {
            console.error(error)
            message.error('创建副本失败')
        } finally {
            setActionSubmitting(false)
        }
    }

    const handleFlowDelete = () => {
        closeContextMenu()
        deleteClick()
    }

    const handleFlowDownload = async () => {
        closeContextMenu()
        setDownLoading(true)
        try {
            const projectList = await getFlowProjectHistoryImagesBatch({
                projectIds: [item?.id],
            })
            const zip = new JSZip()
            const usedZipPaths = new Set()
            let imageCount = 0

            await Promise.all((Array.isArray(projectList) ? projectList : []).map(async (projectItem, projectIndex) => {
                const projectName = sanitizeFileName(projectItem?.projectName || `项目${projectIndex + 1}`) || `项目${projectIndex + 1}`
                const nodes = Array.isArray(projectItem?.nodes) ? projectItem.nodes : []

                await Promise.all(nodes.map(async (nodeItem, nodeIndex) => {
                    const nodeName = sanitizeFileName(nodeItem?.nodeName || `节点${nodeIndex + 1}`) || `节点${nodeIndex + 1}`
                    const images = Array.isArray(nodeItem?.images) ? nodeItem.images.filter(Boolean) : []

                    await Promise.all(images.map(async (imageUrl, imageIndex) => {
                        const response = await fetch(imageUrl)
                        if (!response.ok) throw new Error('下载图片失败')

                        const blob = await response.blob()
                        const extension = getImageExtension(blob, imageUrl)
                        const filePath = getUniqueZipPath(
                            `${projectName}/${nodeName}-${imageIndex + 1}.${extension}`,
                            usedZipPaths,
                        )
                        zip.file(
                            filePath,
                            blob,
                        )
                        imageCount += 1
                    }))
                }))
            }))

            if (imageCount === 0) {
                message.warning('未找到可下载的结果图')
                return
            }

            const zipBlob = await zip.generateAsync({ type: 'blob' })
            saveAs(zipBlob, `${sanitizeFileName(item?.projectName || 'AI创意设计结果图') || 'AI创意设计结果图'}-${Date.now()}.zip`)
            message.success('下载成功')
        } catch (error) {
            console.error(error)
            message.warning('下载失败，请稍后重试')
        } finally {
            setDownLoading(false)
        }
    }

    const projectBatchDownloadMeta = () => {
        projectBatchDownload()
    }

    const projectBatchDownload = throttle(() => {
        if (item?.flowProject) return handleFlowDownload()
        setDownLoading(true)
        commonApiInvoker(API.projectPatchDownload({
            ids: [item?.id],
        }), setDownLoading).then((res) => {
            Utils.downloadFileWithToken(res).then(() => {
                message.success('下载成功')
            }).catch(() => {
                message.warning('下载失败，请稍后重试')
            }).finally(() => {
                setDownLoading(false)
            })
        })
    }, 1000)

    const isCollect = useMemo(() => {
        return item?.id === 'collect'
    }, [item])
    const isCreateFlowCard = !!item?.createFlowCard

    const isVideo = useMemo(() => {
        return IMAGE_TYPE.MODEL_VIDEO == item?.imageType || IMAGE_TYPE.IMAGE_TO_VIDEO == item?.imageType
    }, [item])

    const previewImage = item?.mainImage?.split(',')[0]
    const hasPreviewImage = !!previewImage
    const flowPreviewCells = Array.isArray(item?.previewImages) && item.previewImages.length > 0
        ? [item.previewImages[0] || '', item.previewImages[1] || '']
        : [previewImage || '', '']
    const flowPreviewImages = flowPreviewCells.filter(Boolean)
    const isSingleFlowPreview = flowCreativePreview && flowPreviewImages.length <= 1
    const hideFlowRenameAndDuplicate = item?.flowProject && isEnterpriseMemberTab

    const typeTitle = item?.typeTitle ?? (TYPETITLEMAP[item?.imageType] || '')
    const titleLine = typeTitle ? `${typeTitle} | ${item?.projectName || ''}` : (item?.projectName || '')
    const creatorText = (item.memberName && item.orgData) ? `${item.memberName}` : ''
    const returnTo = item?.flowProject
        ? '/home/myProject/list'
        : ''
    const sourceTab = isEnterpriseMemberTab ? '2' : '1'
    const projectDetailState = item?.flowProject && returnTo ? { returnTo, tab: sourceTab } : undefined
    const freeCanvasQuery = new URLSearchParams({
        project_id: String(item?.id || ''),
        project_name: String(item?.projectName || ''),
        ...(item?.userId ? { project_member_user_id: String(item.userId) } : {}),
        ...(isEnterpriseMemberTab ? { pm_view: '1' } : {}),
        ...(item?.flowProject ? { source_page: 'myProject', source_tab: sourceTab } : {}),
        ...(returnTo ? { return_to: returnTo } : {}),
    }).toString()
    const projectDetailUrl = item?.flowProject
        ? `/home/freeCanvas?${freeCanvasQuery}`
        : `/home/myProject/${item.id}/detail${item?.id === 'collect' ? `?module=${encodeURIComponent(proSidebarValue)}` : ''}`

    if (isCreateFlowCard) {
        return (
            <div className={`${styles.ProjectListItem} ${styles.FlowProjectListItem}`}>
                <div
                    className={`${styles.imgBox} ${styles.flowCreatePreview}`}
                    onClick={() => navigate('/home/freeCanvas?skip_auto_select=1&force_personal_member=1')}
                >
                    <PlusOutlined className={styles.flowCreateIcon} />
                </div>
                <div className={styles.flowMeta}>
                    <p className={styles.flowTitle} title={item?.projectName || '新建项目'}>{item?.projectName || '新建项目'}</p>
                </div>
            </div>
        )
    }

    return (
        <div className={`${styles.ProjectListItem} ${item?.flowProject ? styles.FlowProjectListItem : ''}`}>
            <div
                className={`${styles.imgBox} ${item?.flowProject ? styles.flowPreviewWrap : ''}`}
                onClick={() => navigate(projectDetailUrl, projectDetailState ? { state: projectDetailState } : undefined)}
            >
                {item?.flowProject ? (
                    <div className={`${styles.flowPreviewGrid} ${isSingleFlowPreview ? styles.flowPreviewGridSingle : ''}`}>
                        {flowPreviewCells.map((imageUrl, index) => (
                            <div key={`${item?.id}-${index}`} className={styles.flowPreviewCell}>
                                {imageUrl ? (
                                    <img src={Utils.getImgThumbnail(imageUrl)} alt="" className={styles.flowPreviewImage} />
                                ) : flowCreativePreview ? (
                                    <Iconfont type="icon-aibgOutlined" className={styles.flowPreviewEmptyIcon} />
                                ) : null}
                            </div>
                        ))}
                    </div>
                ) : hasPreviewImage ? (
                    <img src={Utils.getImgThumbnail(previewImage)} alt="" />
                ) : (
                    <div className={styles.EmptyPreview}>
                        <Iconfont type="icon-genimageOutlined" className={styles.EmptyPreviewIcon} />
                    </div>
                )}
                {!isCollect && !item?.flowProject && <div className={styles.hoverMask}>查看合集</div>}
                {isVideo && <i className={styles.VideoIcon}></i>}
                {item.allFailed && (
                    <div className={styles.failMask}>
                        <Iconfont className={styles.iconfont} type="icon-imageErrorOutline" />
                        <p>生成失败</p>
                    </div>
                )}
            </div>
            {item?.flowProject ? (
                <div className={styles.flowTitleRow}>
                    {editingFlowTitle ? (
                        <input
                            ref={renameInputRef}
                            className={styles.flowRenameInput}
                            value={editingValue}
                            maxLength={20}
                            onChange={(event) => setEditingValue(event.target.value)}
                            onClick={(event) => event.stopPropagation()}
                            onBlur={handleFlowRenameSubmit}
                            onKeyDown={(event) => {
                                event.stopPropagation()
                                if (event.key === 'Enter') {
                                    event.preventDefault()
                                    handleFlowRenameSubmit()
                                }
                                if (event.key === 'Escape') {
                                    event.preventDefault()
                                    handleFlowRenameCancel()
                                }
                            }}
                        />
                    ) : (
                        <p className={`${styles.proTitleBox} ${styles.flowTitle}`} title={item?.projectName || ''}>
                            {item?.projectName || ''}
                        </p>
                    )}
                    <button
                        type="button"
                        className={`${styles.flowMenuTrigger} ${contextMenu.visible ? styles.flowMenuTriggerVisible : ''}`}
                        onClick={handleFlowMenuTriggerClick}
                        aria-label="项目菜单"
                    >
                        <EllipsisOutlined />
                    </button>
                </div>
            ) : (
                <p className={styles.proTitleBox} title={titleLine}>{titleLine}</p>
            )}
            {!isCollect && (
                <div className={styles.metaRow}>
                    <p className={`${styles.creatDate} ${item?.flowProject ? styles.flowDate : ''}`}>
                        <span>{item?.flowProject ? item.updatedTime?.split(' ')[0] : item.updatedTime.split(' ')[0]}</span>
                        {!item?.flowProject && creatorText && <span className={styles.creatorText}>{creatorText}</span>}
                    </p>
                    {item?.flowProject && isEnterpriseMemberTab && item?.memberName ? (
                        <span className={styles.flowMemberName} title={item.memberName}>{item.memberName}</span>
                    ) : null}
                    {!item?.flowProject && (
                        <span className={styles.options}>
                            {downLoading ? (
                                <LoadingOutlined className={styles.Icon} />
                            ) : (
                                <DownloadOutlined className={styles.Icon} onClick={projectBatchDownloadMeta} />
                            )}
                            <Iconfont type="icon-DeleteOutlined" className={styles.Icon} onClick={deleteClick} />
                        </span>
                    )}
                </div>
            )}
            {isSelected && (
                <div className={styles.SelectMask} onClick={() => props.selectCurrentItem(item?.id)}>
                    <Checkbox
                        className={styles.ItemSelect}
                        checked={selectedList.includes(item?.id)}
                    />
                </div>
            )}
            {item?.flowProject ? (
                <ProjectContextMenu
                    visible={contextMenu.visible}
                    x={contextMenu.x}
                    y={contextMenu.y}
                    showPin={false}
                    showRename={!hideFlowRenameAndDuplicate}
                    showDuplicate={!hideFlowRenameAndDuplicate}
                    onRename={handleFlowRenameStart}
                    onDuplicate={handleFlowDuplicate}
                    onDownload={handleFlowDownload}
                    onDelete={handleFlowDelete}
                />
            ) : null}
        </div>
    )
}

export default ProjectListItem
